import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import Card from "components/Card/Card";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { useDispatch, useSelector } from "utils/redux";

import { useForm, Controller } from "react-hook-form";
import type { OptionType } from "components/inputs";
import { Select, TextInput, MaskedInput } from "components/inputs";
import ClaimsPageContainer from "./ClaimsPageContainer";
import { setCurrentClaim } from "services/claimsDataSlice.ts";

const ClaimMSA = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm(); // Default for claimant selector);

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      reset(claim);
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claim) {
      reset(claim);
    }
  }, [claim, reset]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        })
          .unwrap()
          .then((result) => {
            dispatch(setCurrentClaim(result));
          });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchClaimById, claimId],
  );

  const constants = useSelector((state) => state.shared.constants);
  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <ClaimsPageContainer>
      <div key={"claimant-form-msa"}>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <Card heading="MSA Information" cardNumber={1}>
              <Controller
                control={control}
                name={"msa_amount"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder={"0.00"}
                    label="MSA Amount"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("msa_amount", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"msa_period"}
                render={({ field }) => (
                  <MaskedInput
                    {...field}
                    placeholder="00"
                    label="MSA Period"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    mask="number"
                    maxLength={2}
                    onBlur={() =>
                      handleSave("msa_period", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"payout_indicator"}
                render={({ field: { name, value } }) => (
                  <Select
                    label="Select Type"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.PAYOUT_INDICATOR || []}
                    value={constants?.PAYOUT_INDICATOR?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) =>
                      handleSave("payout_indicator", o.value)
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"initial_deposit_amount"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder="0.00"
                    label="Initial Deposit Amount"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "initial_deposit_amount",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"anniversary_deposit_amount"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder="0.00"
                    label="Anniversary Deposit Amount"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "anniversary_deposit_amount",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"case_control_number"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder={"X".repeat(15)}
                    label="Case Control Number"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    maxLength={15}
                    onBlur={() =>
                      handleSave("case_control_number", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"professional_administrator_ein"}
                render={({ field }) => (
                  <MaskedInput
                    {...field}
                    placeholder={"X".repeat(9)}
                    label="Professional Admin EIN"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    maxLength={9}
                    mask="number"
                    onBlur={() =>
                      handleSave(
                        "professional_administrator_ein",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
            </Card>
          </div>
        </div>
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimMSA;
